import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import {
  type QueryParameters,
  queryParameterSchema,
} from '../../components/CarFinancePage/queries/schema/carFinance.schema';

import Error from '../../pages/_error';
import { DefaultLayout } from '../../components/Layouts';
import { CarFinancePage } from '../../components/CarFinancePage/components/CarFinancePage';
import { CarFinancePage as CarFinancePageV2 } from '../../components/CarFinancePageV2/CarFinancePage';

import { logger } from '@dist-marketplace-frontend/services';
import { PAGE } from '../../helpers/pages';
import { useVerticalContext } from '../../contexts/VerticalContext';
import { VERTICAL } from '../../helpers/verticals';
import { FEATURE_CAR_FINANCE_UPDATES } from '../../utils';

type CarFinancePageProps = {
  //   this comes from _app.getInitialProps()
  domain?: string;
};

export default function Page(props: Readonly<CarFinancePageProps>) {
  const { query: unsafeQuery, asPath } = useRouter();
  const parsedQuery = useMemo(
    () => queryParameterSchema.safeParse(unsafeQuery),
    [unsafeQuery],
  );

  const { updateVertical } = useVerticalContext();

  useEffect(() => {
    updateVertical(VERTICAL.MOTOR);
  }, [updateVertical]);

  if (parsedQuery.error) {
    /**
     * Since this code is executed twice, on the server and client,
     * we use this condition to log on the server only.
     */
    if (typeof window === 'undefined') {
      logger.warn({
        message: `Invalid query parameters at ${asPath}`,
        route: PAGE.CAR_FINANCE,
        error: parsedQuery.error,
      });
    }
    return <Error statusCode={500} domain={props.domain} />;
  }
  const query = parsedQuery.data;
  return <CarFinancePageAndLayout query={query} {...props} />;
}

const CarFinancePageAndLayout = (
  props: Readonly<CarFinancePageProps & { query: QueryParameters }>,
) => {
  const { domain, ...rest } = props;

  return (
    <DefaultLayout domain={domain} bg="OFFWHITE">
      {FEATURE_CAR_FINANCE_UPDATES ? (
        <CarFinancePageV2 {...rest} />
      ) : (
        <CarFinancePage {...rest} />
      )}
    </DefaultLayout>
  );
};
